import useGetAllClientMarketOrdersInfinite from '@/hooks/query/useGetAllClientMarketOrdersInfinite'
import { createFileRoute } from '@tanstack/react-router'
import { PencilIcon } from 'lucide-react'
import { useState } from 'react'

import { useGetClientMarket } from '@/gen/publicApi'

import PlaceOrderDialog from '@/containers/placeOrderDialog'

import { Card } from '@/components/card'
import Content from '@/components/content'
import { DropdownMenuItem } from '@/components/dropdown-menu'
import OptionMenu from '@/components/optionMenu'
import OrderAmendDialog from '@/components/orderAmendDialog'
import OrderCancelDialog from '@/components/orderCancelDialog'
import OrderSideText from '@/components/orderSideText'
import OrderStateCircle from '@/components/orderStateCircle'
import Spinner from '@/components/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table'
import { useToast } from '@/components/toast/useToast'

import { useSetBreadcrumbs } from '@/state/breadcrumbs'

import MarketHeading from '../-components/marketHeading'
import MarketNavigation from '../-components/marketNavigation'

export const Route = createFileRoute('/_authed/$client_code/markets/$market_code/orders/')({
    component: MarketOrdersComponent,
})

function MarketOrdersComponent() {
    const { client_code, market_code } = Route.useParams()
    const [placeOrderOpen, setPlaceOrderOpen] = useState(false)
    const { data: market } = useGetClientMarket(client_code, market_code, { query: { suspense: true } })
    const { data: marketOrders } = useGetAllClientMarketOrdersInfinite(client_code, market_code)
    const { toast } = useToast()

    useSetBreadcrumbs([
        {
            to: '/$client_code/markets',
            text: 'Markets',
            params: { client_code },
        },
        'seperator',
        {
            to: '/$client_code/markets/$market_code',
            params: {
                market_code,
                client_code,
            },
            text: market?.name || '--',
        },
        'seperator',
        {
            to: '/$client_code/markets/$market_code/orders',
            params: {
                market_code,
                client_code,
            },
            text: 'Orders',
        },
    ])

    if (!market || !marketOrders) {
        return <Spinner size="sm" absolute navVisible />
    }

    const onPlaceOrderSuccess = () => {
        setPlaceOrderOpen(false)
        toast({
            title: 'Order placed successfully!',
            description: 'It may take a few seconds to appear',
        })
    }

    return (
        <>
            <MarketHeading market={market} />
            <Content>
                <MarketNavigation active="orders">
                    <OptionMenu>
                        <DropdownMenuItem className="flex" onClick={() => setPlaceOrderOpen(true)}>
                            <PencilIcon className="w-4 h-4 mr-2" /> Place an Order
                        </DropdownMenuItem>
                    </OptionMenu>
                </MarketNavigation>
                <Card className="flex-grow" hideOverflow>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ClOrdId</TableHead>
                                <TableHead>State</TableHead>
                                <TableHead>Instrument</TableHead>
                                <TableHead>Side</TableHead>
                                <TableHead>Strat</TableHead>
                                <TableHead>Units</TableHead>
                                <TableHead>Price</TableHead>
                                <TableHead>Filled Units</TableHead>
                                <TableHead>Filled Value</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {marketOrders &&
                                marketOrders.map(order => (
                                    <TableRow key={order.clientOrderRef}>
                                        <TableCell>{order.clientOrderRef}</TableCell>
                                        <TableCell>
                                            <OrderStateCircle className="mr-2" state={order.state} />
                                            {order.state}
                                        </TableCell>
                                        <TableCell>{order.instrumentSymbol}</TableCell>
                                        <TableCell>
                                            <OrderSideText side={order.side} />
                                        </TableCell>
                                        <TableCell>{order.targetStrategy}</TableCell>
                                        <TableCell>{order.units}</TableCell>
                                        <TableCell>{order.price}</TableCell>
                                        <TableCell>{order.filledUnits}</TableCell>
                                        <TableCell>{order.filledValue}</TableCell>
                                        <TableCell>
                                            {order.state == 'Open' && (
                                                <div className="flex justify-end">
                                                    <OrderAmendDialog order={order} />
                                                    <OrderCancelDialog order={order} />
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </Card>
            </Content>
            {placeOrderOpen && (
                <PlaceOrderDialog
                    clientCode={client_code}
                    marketCode={market_code}
                    onOpenChange={setPlaceOrderOpen}
                    onSuccess={onPlaceOrderSuccess}
                />
            )}
        </>
    )
}
